import styled, { css } from 'styled-components';
import { baseVariables } from '@marriott/mi-ui-library';
import { OTabContainerAlignment } from './OTabContainer.types';

const { spacers, mediaQuery } = baseVariables;

const singleLineClip = css`
  display: block;
  max-inline-size: 100%;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
`;

const multilineClamp = (lines: number) => css`
  display: -webkit-box;
  max-inline-size: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
`;

/**
 * Wrapper props:
 *  - `$alignment`  Single source of truth for the organism's alignment token. Used by the
 *                  mobile-override rules below to decide whether centered styles should be
 *                  reinstated at `>= md` breakpoints.
 */
export const Wrapper = styled.div<{ $alignment?: OTabContainerAlignment }>`
  display: flex;
  flex-direction: column;
  inline-size: 100%;
  gap: ${spacers?.spacers?.[4] ?? '1.5rem'};
  padding-block-start: ${spacers?.spacers?.[5] ?? '2rem'};

  /*
   * MContentHeader ships with its own bottom padding for standalone use. When composed
   * inside OTabContainer the gap between MContentHeader and MTabGroup is owned by the
   * organism's flex \`gap\` above, so we neutralize the molecule's trailing padding here.
   */
  [data-component-name='m-web-ui-kit-MContentHeader'] {
    padding-block-end: 0;
  }

  [data-component-name='a-web-ui-kit-AEyebrow'] {
    ${singleLineClip}
  }

  [data-component-name='a-web-ui-kit-AHeader'] {
    padding-block-end: ${spacers?.spacers?.[2] ?? '0.5rem'};
  }

  [data-component-name='a-web-ui-kit-AHeader'],
  [data-component-name='a-web-ui-kit-ABody'] {
    ${multilineClamp(3)}
  }

  [data-component-name='m-web-ui-kit-MTabGroup'] .swiper-slide,
  [data-component-name='m-web-ui-kit-MTabControl'] {
    max-inline-size: 100%;
  }

  [data-component-name='m-web-ui-kit-MTabControl'] .m-tab-control-label {
    ${singleLineClip}
  }

  /*
   * Mobile tab-group alignment rule.
   *
   * The tab group always scrolls left-to-right on mobile, so the Swiper strip starts at
   * the inline-start edge regardless of the organism's \`alignment\` token. Force the inner
   * \`MTabControl\` button + its text block to \`flex-start\` on mobile so tabs visually
   * hug the leading edge even when the organism is configured as \`Centered\`. The content
   * header continues to honour the organism's alignment at every breakpoint.
   */
  [data-component-name='m-web-ui-kit-MTabControl'],
  [data-component-name='m-web-ui-kit-MTabControl'] > div {
    align-items: flex-start;
  }

  /*
   * At \`>= md\` viewports re-enable centered tab-control alignment when the organism asks
   * for it. The molecule still renders \`align-items: center\` inline via its styled-props,
   * but we make the behaviour explicit (and stable against future molecule refactors) by
   * mirroring it here scoped to the organism.
   */
  @media ${mediaQuery.md} {
    [data-component-name='a-web-ui-kit-AHeader'],
    [data-component-name='a-web-ui-kit-ABody'] {
      ${multilineClamp(2)}
    }

    ${({ $alignment }) =>
      ($alignment === OTabContainerAlignment.Centered || $alignment === 'Center') &&
      `
      [data-component-name='m-web-ui-kit-MTabControl'],
      [data-component-name='m-web-ui-kit-MTabControl'] > div {
        align-items: center;
      }
    `}
  }
`;
